import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Chip, Grid, Select, MenuItem, IconButton, Modal,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useDropzone } from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import { connect } from 'react-redux';
import { UpdateUserPortfolioItemsCommand, UserPortfolioItemViewModel } from '../../../../configuration/api/apiSchema';
import { getUserPortfolio, updateUserPortfolio } from './actions';
import { getUserCategories } from '../../../Categories/actions';
import { HandymanAppState } from '../../../../configuration/redux/reducers';
import PreviewImageModal from '../../../../components/PreviewImage/PreviewImageModal';

interface FileWithPreview {
    src: string;
    file?: File;
}

interface UserPortfolioProps {
    isLoading: boolean;
    isListUpdated: boolean;
    isCategoriesLoading: boolean;
    userPortfolioItems?: UserPortfolioItemViewModel[];
    userCategories?: { key: string; name: string }[];
    authData?: { id: string; name: string };
    getUserPortfolio: (userId?: string) => void;
    updateUserPortfolio: (command: UpdateUserPortfolioItemsCommand) => void;
    getUserCategories: (userId?: string) => void;
    userId?: string;
}

const UserPortfolio: React.FC<UserPortfolioProps> = (props) => {
    const isReadonly = props.userId !== undefined;

    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [photos, setPhotos] = useState<FileWithPreview[]>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        if (!props.userCategories && !props.isCategoriesLoading) {
            if (props.userId) {
                props.getUserCategories(props.userId);
            }
            else {
                props.getUserCategories();
            }
        }

        if (props.userId) {
            props.getUserPortfolio(props.userId);
        }
        else if (props.authData) {
            props.getUserPortfolio();
        }

        if (props.isListUpdated) {
            handleCloseModal();
        }

    }, [props.userCategories, props.isCategoriesLoading, props.authData, props.isListUpdated]);

    const handleImageClick = (src: string): void => {
        setSelectedImage(src);
        setIsImageModalOpen(true);
    };

    const handleCloseImageModal = (): void => {
        setIsImageModalOpen(false);
        setSelectedImage(null);
    };

    const handleOpenModal = (index: number | null = null): void => {
        if (index !== null && props.userPortfolioItems) {
            const item = props.userPortfolioItems[index];
            setName(item.name || '');
            setDescription(item.description || '');
            setTags(item.categoryKeys || []);
            setPhotos((item.base64Images || []).map((src, idx) => ({ src, id: idx })));
        } else {
            setName('');
            setDescription('');
            setTags([]);
            setPhotos([]);
        }
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsModalOpen(false);
    };

    const handleSave = (): void => {
        const updatedItems = [...(props.userPortfolioItems || [])];
        const newItem: UserPortfolioItemViewModel = {
            name,
            description,
            categoryKeys: tags,
            base64Images: photos.map((photo) => photo.src)
        };

        if (editingIndex !== null) {
            updatedItems[editingIndex] = newItem;
        } else {
            updatedItems.push(newItem);
        }

        props.updateUserPortfolio({ userPortfolioItems: updatedItems });
    };

    const handleRemovePortfolioItem = (index: number): void => {
        const updatedItems = [...(props.userPortfolioItems || [])];
        updatedItems.splice(index, 1);
        props.updateUserPortfolio({ userPortfolioItems: updatedItems });
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'image/*': [] },
        multiple: true,
        onDrop: async (acceptedFiles) => {
            const base64Files = await Promise.all(
                acceptedFiles.map((file) =>
                    fileToBase64(file).then((base64) => ({
                        src: base64, // Use the base64 string
                        file,
                    }))
                )
            );
            setPhotos((prev) => [...prev, ...base64Files]);
        },
    });

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string); // Convert to base64
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file); // Trigger the conversion
        });
    };

    return (
        <Box>
            <PreviewImageModal
                base64SelectedImage={selectedImage}
                handleCloseImageModal={handleCloseImageModal}
                isImageModalOpen={isImageModalOpen}
            />

            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <Box
                    p={4}
                    sx={{
                        backgroundColor: 'white',
                        margin: 'auto',
                        width: { xs: '90%', sm: '70%', md: '40%' },
                        borderRadius: 2,
                        position: 'relative',
                        mt: { xs: '10%', sm: '5%' },
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h5" gutterBottom>
                        {editingIndex !== null ? 'Edit' : 'Add'} Portfolio Item
                    </Typography>

                    <TextField
                        label="Name"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <TextField
                        label="Description"
                        multiline
                        fullWidth
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />

                    <Select
                        fullWidth
                        multiple
                        value={tags}
                        onChange={(e) =>
                            setTags(
                                typeof e.target.value === 'string'
                                    ? e.target.value.split(',')
                                    : e.target.value
                            )
                        }
                        style={{ marginBottom: 10 }}
                    >
                        {(props.userCategories || []).map((category) => (
                            <MenuItem key={category.key} value={category.key}>
                                <WorkIcon fontSize="small" sx={{ mr: 1 }} />
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>

                    <Box {...getRootProps()} mt={2} p={2} sx={{ border: '2px dashed #ccc', textAlign: 'center' }}>
                        <input {...getInputProps()} />
                        <Typography>Drag and drop files here, or click to select files</Typography>
                    </Box>

                    <Grid container spacing={2} mt={2}>
                        {photos.map((photo, index) => (
                            <Grid item key={index} xs={6} sm={4}>
                                <Box position="relative">
                                    <img
                                        src={photo.src}
                                        alt={`Uploaded ${index}`}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleImageClick(photo.src)}
                                    />
                                    <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() =>
                                            setPhotos((prev) => prev.filter((_, i) => i !== index))
                                        }
                                        sx={{ position: 'absolute', top: 4, right: 4 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{ mt: 2, width: '100%' }}
                    >
                        {props.isLoading ? (
                            <CircularProgress
                                size={24}
                                style={{
                                    color: "white",
                                    position: "absolute",
                                }}
                            />
                        ) : (
                            "Save"
                        )}
                    </Button>
                </Box>
            </Modal>

            {/* Portfolio List */}
            <Typography variant="h6" gutterBottom>
                Portfolio Items
            </Typography>

            {
                !isReadonly && <Button variant="contained" onClick={() => handleOpenModal()} sx={{ mt: 2, mb: 2 }}>
                    Add Item
                </Button>
            }

            {props.userPortfolioItems?.length ? (
                <Grid container spacing={2}>
                    {props.userPortfolioItems.map((item, index) => (
                        <Grid item key={index} xs={12} sm={6} md={4}>
                            <Box
                                p={2}
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                            >
                                {
                                    !isReadonly && <IconButton
                                        onClick={() => handleOpenModal(index)}
                                        size="small"
                                        sx={{ position: 'absolute', top: 8, right: 52 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                }
                                {
                                    !isReadonly && <IconButton
                                        onClick={() => handleRemovePortfolioItem(index)}
                                        size="small"
                                        color="error"
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }

                                <Typography variant="h6">{item.name}</Typography>
                                <Typography>{item.description}</Typography>

                                <Box mt={2}>
                                    {(item.categoryKeys || []).map((tag) => (
                                        <Chip key={tag} label={tag} sx={{ mr: 1, mb: 1 }} />
                                    ))}
                                </Box>

                                <Grid container spacing={1} mt={1}>
                                    {(item.base64Images || []).map((src, i) => (
                                        <Grid item key={i} xs={6}>
                                            <img
                                                src={src}
                                                alt={`Portfolio ${index} Photo ${i}`}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleImageClick(src)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Typography variant="h6" color="textSecondary" sx={{ mt: 4, textAlign: 'center' }}>
                    There is nothing here. {!isReadonly && 'Please add a portfolio item.'}
                </Typography>
            )}
        </Box>
    );
};

const mapStateToProps = (state: HandymanAppState | any) => ({
    isLoading: state.userPortfolio.isLoading,
    userPortfolioItems: state.userPortfolio.userPortfolioItems,
    userCategories: state.category.userCategories,
    isCategoriesLoading: state.category.isLoading,
    authData: state.user.authData,
    isListUpdated: state.userPortfolio.isListUpdated
});

export default connect(mapStateToProps, {
    getUserPortfolio,
    updateUserPortfolio,
    getUserCategories,
})(UserPortfolio);
